import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    MdArrowBack,
    MdCheckCircle,
    MdCancel,
    MdMerge,
    MdInventory,
    MdCategory,
    MdQrCode,
    MdInfo,
    MdWarning,
    MdSearch,
} from 'react-icons/md';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import {
    fetchManufacturerPendingProduct,
    approveManufacturerProduct,
    rejectManufacturerProduct,
    mergeManufacturerProduct,
    searchManufacturerProducts,
} from '../../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { SimpleSelect } from '../../../../shared/layout/forms/AppSelect';

const ManufacturerProductApprovalDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');

    // Merge functionality
    const [showMergeSearch, setShowMergeSearch] = useState(false);
    const [mergeSearch, setMergeSearch] = useState('');
    const [mergeResults, setMergeResults] = useState([]);
    const [selectedMergeTarget, setSelectedMergeTarget] = useState(null);
    const [searchingMerge, setSearchingMerge] = useState(false);

    // Rejection
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    const loadProduct = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchManufacturerPendingProduct(id);
            setProduct(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadProduct();
    }, [loadProduct]);

    const handleSearchMergeTargets = async () => {
        if (!mergeSearch.trim()) return;
        try {
            setSearchingMerge(true);
            const data = await searchManufacturerProducts({
                search: mergeSearch,
                exclude_virtual: true,
                exclude_id: id,
            });
            setMergeResults(data?.data || data || []);
        } catch (err) {
            console.error('Failed to search products:', err);
        } finally {
            setSearchingMerge(false);
        }
    };

    const handleApprove = async () => {
        if (processing) return;
        try {
            setProcessing(true);
            await approveManufacturerProduct(id, { notes: reviewNotes });
            navigate('/product-approvals', { state: { message: t('ProductApprovalDetail.approveSuccess') } });
        } catch (err) {
            setError(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (processing || !rejectionReason.trim()) return;
        try {
            setProcessing(true);
            await rejectManufacturerProduct(id, {
                reason: rejectionReason,
                notes: reviewNotes,
            });
            setShowRejectModal(false);
            navigate('/product-approvals', { state: { message: t('ProductApprovalDetail.rejectSuccess') } });
        } catch (err) {
            setError(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleMerge = async () => {
        if (processing || !selectedMergeTarget) return;
        try {
            setProcessing(true);
            await mergeManufacturerProduct(id, {
                merge_into_id: selectedMergeTarget.id,
                notes: reviewNotes,
            });
            navigate('/product-approvals', { state: { message: t('ProductApprovalDetail.mergeSuccess') } });
        } catch (err) {
            setError(err);
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { label: t('ProductApprovals.status.pending'), color: '#F59E0B', bg: '#FEF3C7' },
            approved: { label: t('ProductApprovals.status.approved'), color: '#22C55E', bg: '#DCFCE7' },
            rejected: { label: t('ProductApprovals.status.rejected'), color: '#EF4444', bg: '#FEE2E2' },
            merged: { label: t('ProductApprovals.status.merged'), color: '#3B82F6', bg: '#DBEAFE' },
        };
        const config = statusConfig[status] || { label: status, color: '#6B7280', bg: '#F3F4F6' };
        return <StatusBadge $color={config.color} $bg={config.bg}>{config.label}</StatusBadge>;
    };

    if (loading) return <LoadingMessage>{t('ProductApprovalDetail.loading')}</LoadingMessage>;
    if (error) return <ErrorMessage>{t('ProductApprovalDetail.error')}</ErrorMessage>;
    if (!product) return <ErrorMessage>{t('ProductApprovalDetail.notFound')}</ErrorMessage>;

    const isPending = product.approval_status === 'pending';

    return (
        <PageContainer>
            <PageHeader>
                <HeaderRow>
                    <BackButton onClick={() => navigate('/product-approvals')}>
                        <MdArrowBack size={20} />
                        {t('ProductApprovalDetail.back')}
                    </BackButton>
                    <HeaderInfo>
                        <Title>{t('ProductApprovalDetail.title')}</Title>
                        {getStatusBadge(product.approval_status)}
                    </HeaderInfo>
                </HeaderRow>
            </PageHeader>

            <PageContent>
                <ContentGrid>
                    {/* Left Column - Product Details */}
                    <MainColumn>
                        {/* Product Overview */}
                        <Section>
                            <SectionHeader>
                                <MdInventory size={20} />
                                <SectionTitle>{t('ProductApprovalDetail.sections.productInfo')}</SectionTitle>
                                <VirtualBadge>{t('ProductApprovalDetail.virtualProduct')}</VirtualBadge>
                            </SectionHeader>
                            <ProductOverview>
                                <ProductImageLarge
                                    src={product.image_url || '/placeholder-product.png'}
                                    alt={product.product_name}
                                    onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                                />
                                <ProductDetails>
                                    <ProductName>{product.product_name || '-'}</ProductName>
                                    {product.model_number && (
                                        <ProductModel>{t('ProductApprovalDetail.fields.model')}: {product.model_number}</ProductModel>
                                    )}
                                    {product.brand && (
                                        <ProductBrand>{t('ProductApprovalDetail.fields.brand')}: {product.brand}</ProductBrand>
                                    )}
                                </ProductDetails>
                            </ProductOverview>
                        </Section>

                        {/* Identifiers */}
                        <Section>
                            <SectionHeader>
                                <MdQrCode size={20} />
                                <SectionTitle>{t('ProductApprovalDetail.sections.identifiers')}</SectionTitle>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>EAN</InfoLabel>
                                    <InfoValue>{product.ean || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>UPC</InfoLabel>
                                    <InfoValue>{product.upc || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>GTIN</InfoLabel>
                                    <InfoValue>{product.gtin || '-'}</InfoValue>
                                </InfoItem>
                            </InfoGrid>
                        </Section>

                        {/* Category */}
                        <Section>
                            <SectionHeader>
                                <MdCategory size={20} />
                                <SectionTitle>{t('ProductApprovalDetail.sections.category')}</SectionTitle>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>{t('ProductApprovalDetail.fields.category')}</InfoLabel>
                                    <InfoValue>{product.category_core?.path || product.category_core?.name || '-'}</InfoValue>
                                </InfoItem>
                                {product.category_core?.parent && (
                                    <InfoItem>
                                        <InfoLabel>{t('ProductApprovalDetail.fields.parentCategory')}</InfoLabel>
                                        <InfoValue>{product.category_core.parent.name}</InfoValue>
                                    </InfoItem>
                                )}
                            </InfoGrid>
                        </Section>

                        {/* Additional Info */}
                        {product.description && (
                            <Section>
                                <SectionHeader>
                                    <MdInfo size={20} />
                                    <SectionTitle>{t('ProductApprovalDetail.sections.description')}</SectionTitle>
                                </SectionHeader>
                                <Description>{product.description}</Description>
                            </Section>
                        )}

                        {/* Extracted Data */}
                        {product.extracted_data && (
                            <Section>
                                <SectionHeader>
                                    <MdInfo size={20} />
                                    <SectionTitle>{t('ProductApprovalDetail.sections.extractedData')}</SectionTitle>
                                </SectionHeader>
                                <ExtractedDataBox>
                                    <pre>{JSON.stringify(product.extracted_data, null, 2)}</pre>
                                </ExtractedDataBox>
                            </Section>
                        )}
                    </MainColumn>

                    {/* Right Column - Actions */}
                    <SideColumn>
                        {/* Source Info */}
                        <Section>
                            <SectionHeader>
                                <SectionTitle>{t('ProductApprovalDetail.sections.sourceInfo')}</SectionTitle>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>{t('ProductApprovalDetail.fields.source')}</InfoLabel>
                                    <InfoValue>
                                        {product.virtual_source === 'scanned_bill'
                                            ? t('ProductApprovalDetail.source.scannedBill')
                                            : product.virtual_source || '-'
                                        }
                                    </InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('ProductApprovalDetail.fields.submittedAt')}</InfoLabel>
                                    <InfoValue>{new Date(product.created_at).toLocaleString()}</InfoValue>
                                </InfoItem>
                            </InfoGrid>
                        </Section>

                        {/* Review Notes */}
                        {isPending && (
                            <Section>
                                <SectionHeader>
                                    <SectionTitle>{t('ProductApprovalDetail.sections.reviewNotes')}</SectionTitle>
                                </SectionHeader>
                                <NotesTextarea
                                    placeholder={t('ProductApprovalDetail.notesPlaceholder')}
                                    value={reviewNotes}
                                    onChange={(e) => setReviewNotes(e.target.value)}
                                />
                            </Section>
                        )}

                        {/* Actions */}
                        {isPending && (
                            <ActionsSection>
                                <ActionTitle>{t('ProductApprovalDetail.sections.actions')}</ActionTitle>

                                {/* Approve */}
                                <ActionCard>
                                    <ActionHeader>
                                        <MdCheckCircle size={20} color="#22C55E" />
                                        <span>{t('ProductApprovalDetail.actions.approve.title')}</span>
                                    </ActionHeader>
                                    <ActionDescription>
                                        {t('ProductApprovalDetail.actions.approve.description')}
                                    </ActionDescription>
                                    <ApproveButton onClick={handleApprove} disabled={processing}>
                                        {processing ? t('ProductApprovalDetail.processing') : t('ProductApprovalDetail.actions.approve.button')}
                                    </ApproveButton>
                                </ActionCard>

                                {/* Merge */}
                                <ActionCard>
                                    <ActionHeader>
                                        <MdMerge size={20} color="#3B82F6" />
                                        <span>{t('ProductApprovalDetail.actions.merge.title')}</span>
                                    </ActionHeader>
                                    <ActionDescription>
                                        {t('ProductApprovalDetail.actions.merge.description')}
                                    </ActionDescription>

                                    {!showMergeSearch ? (
                                        <MergeButton onClick={() => setShowMergeSearch(true)}>
                                            <MdSearch size={18} />
                                            {t('ProductApprovalDetail.actions.merge.searchButton')}
                                        </MergeButton>
                                    ) : (
                                        <MergeSearchSection>
                                            <SearchRow>
                                                <SearchInput
                                                    type="text"
                                                    placeholder={t('ProductApprovalDetail.actions.merge.searchPlaceholder')}
                                                    value={mergeSearch}
                                                    onChange={(e) => setMergeSearch(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchMergeTargets()}
                                                />
                                                <SearchButton onClick={handleSearchMergeTargets} disabled={searchingMerge}>
                                                    <MdSearch size={18} />
                                                </SearchButton>
                                            </SearchRow>

                                            {mergeResults.length > 0 && (
                                                <MergeResultsList>
                                                    {mergeResults.map((result) => (
                                                        <MergeResultItem
                                                            key={result.id}
                                                            $selected={selectedMergeTarget?.id === result.id}
                                                            onClick={() => setSelectedMergeTarget(result)}
                                                        >
                                                            <MergeResultImage
                                                                src={result.image_url || '/placeholder-product.png'}
                                                                alt={result.product_name}
                                                            />
                                                            <MergeResultInfo>
                                                                <span className="name">{result.product_name}</span>
                                                                <span className="model">{result.model_number}</span>
                                                            </MergeResultInfo>
                                                            {selectedMergeTarget?.id === result.id && (
                                                                <MdCheckCircle size={20} color="#22C55E" />
                                                            )}
                                                        </MergeResultItem>
                                                    ))}
                                                </MergeResultsList>
                                            )}

                                            {selectedMergeTarget && (
                                                <ConfirmMergeButton onClick={handleMerge} disabled={processing}>
                                                    {processing
                                                        ? t('ProductApprovalDetail.processing')
                                                        : t('ProductApprovalDetail.actions.merge.confirmButton')
                                                    }
                                                </ConfirmMergeButton>
                                            )}
                                        </MergeSearchSection>
                                    )}
                                </ActionCard>

                                {/* Reject */}
                                <ActionCard>
                                    <ActionHeader>
                                        <MdCancel size={20} color="#EF4444" />
                                        <span>{t('ProductApprovalDetail.actions.reject.title')}</span>
                                    </ActionHeader>
                                    <ActionDescription>
                                        {t('ProductApprovalDetail.actions.reject.description')}
                                    </ActionDescription>
                                    <RejectButton onClick={() => setShowRejectModal(true)} disabled={processing}>
                                        {t('ProductApprovalDetail.actions.reject.button')}
                                    </RejectButton>
                                </ActionCard>
                            </ActionsSection>
                        )}

                        {/* Already Reviewed */}
                        {!isPending && (
                            <ReviewedSection>
                                <ReviewedHeader $status={product.approval_status}>
                                    {product.approval_status === 'approved' && <MdCheckCircle size={24} />}
                                    {product.approval_status === 'rejected' && <MdCancel size={24} />}
                                    {product.approval_status === 'merged' && <MdMerge size={24} />}
                                    <span>
                                        {product.approval_status === 'approved' && t('ProductApprovalDetail.alreadyApproved')}
                                        {product.approval_status === 'rejected' && t('ProductApprovalDetail.alreadyRejected')}
                                        {product.approval_status === 'merged' && t('ProductApprovalDetail.alreadyMerged')}
                                    </span>
                                </ReviewedHeader>
                                {product.approved_at && (
                                    <ReviewedInfo>
                                        <span>{t('ProductApprovalDetail.reviewedAt')}</span>
                                        <strong>{new Date(product.approved_at).toLocaleString()}</strong>
                                    </ReviewedInfo>
                                )}
                                {product.merged_into_id && (
                                    <ReviewedInfo>
                                        <span>{t('ProductApprovalDetail.mergedInto')}</span>
                                        <MergedLink onClick={() => navigate(`/products/${product.merged_into_id}`)}>
                                            {t('ProductApprovalDetail.viewMergedProduct')}
                                        </MergedLink>
                                    </ReviewedInfo>
                                )}
                            </ReviewedSection>
                        )}
                    </SideColumn>
                </ContentGrid>
            </PageContent>

            {/* Rejection Modal */}
            {showRejectModal && (
                <ModalOverlay onClick={() => setShowRejectModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <MdCancel size={24} color="#EF4444" />
                            <h3>{t('ProductApprovalDetail.rejectModal.title')}</h3>
                        </ModalHeader>
                        <ModalBody>
                            <p>{t('ProductApprovalDetail.rejectModal.description')}</p>
                            <ReasonSelect
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                            >
                                <option value="">{t('ProductApprovalDetail.rejectModal.selectReason')}</option>
                                <option value="not_our_product">{t('ProductApprovalDetail.rejectModal.reasons.notOurProduct')}</option>
                                <option value="duplicate">{t('ProductApprovalDetail.rejectModal.reasons.duplicate')}</option>
                                <option value="incorrect_info">{t('ProductApprovalDetail.rejectModal.reasons.incorrectInfo')}</option>
                                <option value="counterfeit">{t('ProductApprovalDetail.rejectModal.reasons.counterfeit')}</option>
                                <option value="discontinued">{t('ProductApprovalDetail.rejectModal.reasons.discontinued')}</option>
                                <option value="other">{t('ProductApprovalDetail.rejectModal.reasons.other')}</option>
                            </ReasonSelect>
                        </ModalBody>
                        <ModalFooter>
                            <CancelButton onClick={() => setShowRejectModal(false)}>
                                {t('ProductApprovalDetail.rejectModal.cancel')}
                            </CancelButton>
                            <ConfirmRejectButton
                                onClick={handleReject}
                                disabled={!rejectionReason.trim() || processing}
                            >
                                {processing ? t('ProductApprovalDetail.processing') : t('ProductApprovalDetail.rejectModal.confirm')}
                            </ConfirmRejectButton>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

export default ManufacturerProductApprovalDetail;

// Styled Components
const HeaderRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: #6B7280;
    font-size: 0.875rem;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: rgba(130, 22, 146, 1);
    }
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const Title = styled.h1`
    margin: 0;
    color: rgba(130, 22, 146, 1);
    font-size: 1.5rem;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.875rem;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 1.5rem;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const MainColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const SideColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Section = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 1rem;
    color: rgba(130, 22, 146, 1);
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
`;

const VirtualBadge = styled.span`
    padding: 0.25rem 0.625rem;
    background: #FEF3C7;
    color: #92400E;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    margin-left: auto;
`;

const ProductOverview = styled.div`
    display: flex;
    gap: 1.25rem;
`;

const ProductImageLarge = styled.img`
    width: 160px;
    height: 160px;
    object-fit: cover;
    border-radius: 12px;
    background: #F3F4F6;
`;

const ProductDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const ProductName = styled.h2`
    margin: 0;
    font-size: 1.375rem;
    color: #111827;
`;

const ProductModel = styled.span`
    font-size: 0.875rem;
    color: #6B7280;
`;

const ProductBrand = styled.span`
    font-size: 0.875rem;
    color: #6B7280;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const InfoLabel = styled.span`
    font-size: 0.75rem;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
    font-size: 0.9375rem;
    color: #111827;
`;

const Description = styled.p`
    margin: 0;
    color: #374151;
    line-height: 1.6;
`;

const ExtractedDataBox = styled.div`
    background: #F9FAFB;
    border-radius: 8px;
    padding: 1rem;
    overflow-x: auto;

    pre {
        margin: 0;
        font-size: 0.75rem;
        color: #374151;
        white-space: pre-wrap;
    }
`;

const NotesTextarea = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 0.75rem;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: rgba(130, 22, 146, 0.5);
        box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.1);
    }
`;

const ActionsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const ActionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
`;

const ActionCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ActionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
`;

const ActionDescription = styled.p`
    margin: 0 0 0.75rem 0;
    font-size: 0.8125rem;
    color: #6B7280;
`;

const ApproveButton = styled.button`
    width: 100%;
    padding: 0.75rem;
    background: #22C55E;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
        background: #16A34A;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const MergeButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem;
    background: #3B82F6;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: #2563EB;
    }
`;

const MergeSearchSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const SearchRow = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const SearchInput = styled.input`
    flex: 1;
    padding: 0.625rem 0.75rem;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;

    &:focus {
        outline: none;
        border-color: #3B82F6;
    }
`;

const SearchButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #3B82F6;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #2563EB;
    }

    &:disabled {
        opacity: 0.6;
    }
`;

const MergeResultsList = styled.div`
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
`;

const MergeResultItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem;
    cursor: pointer;
    background: ${({ $selected }) => $selected ? '#EFF6FF' : 'transparent'};
    border-bottom: 1px solid #E5E7EB;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${({ $selected }) => $selected ? '#EFF6FF' : '#F9FAFB'};
    }
`;

const MergeResultImage = styled.img`
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 6px;
    background: #F3F4F6;
`;

const MergeResultInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;

    .name {
        font-weight: 500;
        color: #111827;
        font-size: 0.875rem;
    }

    .model {
        font-size: 0.75rem;
        color: #6B7280;
    }
`;

const ConfirmMergeButton = styled.button`
    width: 100%;
    padding: 0.75rem;
    background: #3B82F6;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #2563EB;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const RejectButton = styled.button`
    width: 100%;
    padding: 0.75rem;
    background: white;
    color: #EF4444;
    border: 2px solid #EF4444;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: #FEE2E2;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ReviewedSection = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ReviewedHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
    background: ${({ $status }) =>
        $status === 'approved' ? '#DCFCE7' :
        $status === 'merged' ? '#DBEAFE' : '#FEE2E2'
    };
    color: ${({ $status }) =>
        $status === 'approved' ? '#166534' :
        $status === 'merged' ? '#1E40AF' : '#991B1B'
    };
    border-radius: 8px;
    margin-bottom: 1rem;
    font-weight: 600;
`;

const ReviewedInfo = styled.div`
    margin-bottom: 0.75rem;

    span {
        display: block;
        font-size: 0.75rem;
        color: #6B7280;
        margin-bottom: 0.25rem;
    }

    strong {
        color: #374151;
    }
`;

const MergedLink = styled.button`
    background: none;
    border: none;
    color: #3B82F6;
    font-weight: 500;
    cursor: pointer;
    padding: 0;

    &:hover {
        text-decoration: underline;
    }
`;

// Modal Styles
const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 480px;
    margin: 1.25rem;
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.25rem;
    border-bottom: 1px solid #E5E7EB;

    h3 {
        margin: 0;
        font-size: 1.125rem;
        color: #111827;
    }
`;

const ModalBody = styled.div`
    padding: 1.25rem;

    p {
        margin: 0 0 1rem 0;
        color: #6B7280;
    }
`;

const ReasonSelect = styled(SimpleSelect)`
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #EF4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem;
    border-top: 1px solid #E5E7EB;
`;

const CancelButton = styled.button`
    padding: 0.625rem 1.25rem;
    background: white;
    color: #374151;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        background: #F3F4F6;
    }
`;

const ConfirmRejectButton = styled.button`
    padding: 0.625rem 1.25rem;
    background: #EF4444;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #DC2626;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
